// One seat's lines (spec 06 §Main screens 7: "edit panel = permissions/delegation editor"): who it
// reports to, which seats it approves for (authority) and hands work to (delegation), with each
// delegation's limits (05 §Delegation rules: depth, budget, task kinds). Everything here goes into
// the editor's draft, the same draft dragging writes: nothing is saved until the review. It is
// also the keyboard and touch way to move a seat, which dragging is not.
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  NativeSelect,
  NativeSelectOption,
} from "@companyos/ui";
import { PlusIcon, XIcon } from "lucide-react";
import { useId, useState } from "react";
import {
  type ChartSeat,
  type DraftOp,
  descendants,
  type EdgePolicy,
  LINE_KINDS,
  LINE_VERBS,
  type Line,
  type LineKind,
  linesOf,
  type OrgChart,
  supervisors,
} from "./chart-model";

export function SeatLinesDialog({
  chart,
  ops,
  seat,
  onOp,
  onClose,
}: {
  chart: OrgChart;
  ops: readonly DraftOp[];
  seat: ChartSeat;
  onOp: (op: DraftOp) => void;
  onClose: () => void;
}) {
  const id = useId();
  const sup = supervisors(chart, ops);
  const below = descendants(seat.id, sup);
  const others = [...chart.seats]
    .filter((s) => s.id !== seat.id && s.status !== "closed")
    .sort((a, b) => a.title.localeCompare(b.title));
  const lines = linesOf(chart, ops).filter((l) => l.from === seat.id);
  const titleOf = (sid: string) => chart.seats.find((s) => s.id === sid)?.title ?? "A seat";

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[600px]" data-slot="seat-lines">
        <DialogHeader>
          <DialogTitle>Lines of {seat.title}</DialogTitle>
          <DialogDescription>
            Changes join the chart's unsaved changes; review them before they are saved.
          </DialogDescription>
        </DialogHeader>
        <div className="flex min-w-0 flex-col gap-5">
          <Field>
            <FieldLabel htmlFor={`${id}-sup`}>Reports to</FieldLabel>
            <NativeSelect
              id={`${id}-sup`}
              value={sup.get(seat.id) ?? ""}
              disabled={seat.status === "closed"}
              onChange={(e) => onOp({ op: "reparent", seat: seat.id, to: e.target.value || null })}
            >
              <NativeSelectOption value="">Nobody (top of the chart)</NativeSelectOption>
              {others
                .filter((s) => !below.has(s.id))
                .map((s) => (
                  <NativeSelectOption key={s.id} value={s.id}>
                    {s.title}
                  </NativeSelectOption>
                ))}
            </NativeSelect>
            <FieldDescription>
              {seat.status === "closed"
                ? "A closed seat's reporting line is frozen."
                : "Seats that report to this one are not offered: the line would go in a circle."}
            </FieldDescription>
          </Field>
          {LINE_KINDS.map((kind) => (
            <KindLines
              key={kind}
              kind={kind}
              lines={lines.filter((l) => l.kind === kind)}
              targets={others}
              titleOf={titleOf}
              onOp={(op) => onOp(op)}
              from={seat.id}
            />
          ))}
        </div>
        <DialogFooter>
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KindLines({
  kind,
  from,
  lines,
  targets,
  titleOf,
  onOp,
}: {
  kind: LineKind;
  from: string;
  lines: Line[];
  targets: ChartSeat[];
  titleOf: (id: string) => string;
  onOp: (op: DraftOp) => void;
}) {
  const id = useId();
  const [pick, setPick] = useState("");
  const free = targets.filter((t) => !lines.some((l) => l.to === t.id));
  return (
    <section aria-labelledby={`${id}-h`} className="flex min-w-0 flex-col gap-2">
      <h3 id={`${id}-h`} className="text-sm font-medium text-foreground">
        {LINE_VERBS[kind]}
      </h3>
      <p className="text-xs text-muted-foreground">
        {kind === "authority"
          ? "This seat may approve for these seats. Approval belongs to a person: a seat held only by a digital employee cannot hold it."
          : "This seat may hand work to these seats, within the limits set here."}
      </p>
      {lines.length ? (
        <ul className="flex flex-col gap-2" data-lines={kind}>
          {lines.map((l) => (
            <li
              key={l.to}
              className="flex min-w-0 flex-col gap-2 rounded-md border border-border bg-card p-2.5"
            >
              <div className="flex min-w-0 items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-sm">{titleOf(l.to)}</span>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Remove: ${LINE_VERBS[kind].toLowerCase()} ${titleOf(l.to)}`}
                  onClick={() => onOp({ op: "remove", kind, from, to: l.to })}
                >
                  <XIcon aria-hidden="true" />
                </Button>
              </div>
              {kind === "delegation" ? (
                <PolicyFields
                  policy={l.policy}
                  name={titleOf(l.to)}
                  onChange={(policy) => onOp({ op: "policy", kind, from, to: l.to, policy })}
                />
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">None.</p>
      )}
      <div className="flex min-w-0 items-end gap-2">
        <Field className="min-w-0 flex-1">
          <FieldLabel htmlFor={`${id}-add`} className="sr-only">
            Add a seat: {LINE_VERBS[kind].toLowerCase()}
          </FieldLabel>
          <NativeSelect
            id={`${id}-add`}
            value={pick}
            onChange={(e) => setPick(e.target.value)}
            className="w-full"
          >
            <NativeSelectOption value="">Choose a seat…</NativeSelectOption>
            {free.map((t) => (
              <NativeSelectOption key={t.id} value={t.id}>
                {t.title}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </Field>
        <Button
          variant="secondary"
          disabled={!pick}
          onClick={() => {
            onOp({ op: "add", kind, from, to: pick });
            setPick("");
          }}
        >
          <PlusIcon aria-hidden="true" />
          Add
        </Button>
      </div>
    </section>
  );
}

/** A delegation's limits: depth (default 2), budget per delegation, task kinds. */
function PolicyFields({
  policy,
  name,
  onChange,
}: {
  policy: EdgePolicy;
  name: string;
  onChange: (policy: EdgePolicy) => void;
}) {
  const id = useId();
  const set = (patch: Partial<EdgePolicy>) => {
    const next: Record<string, unknown> = { ...policy, ...patch };
    for (const k of Object.keys(next)) if (next[k] === undefined) delete next[k];
    onChange(next as EdgePolicy);
  };
  return (
    <div className="grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-3">
      <Field>
        <FieldLabel htmlFor={`${id}-d`} className="text-xs">
          Depth
        </FieldLabel>
        <Input
          id={`${id}-d`}
          type="number"
          min={1}
          max={10}
          placeholder="2"
          aria-label={`Delegation depth to ${name}`}
          value={policy.max_depth ?? ""}
          onChange={(e) => {
            const n = Number.parseInt(e.target.value, 10);
            set({ max_depth: Number.isFinite(n) ? Math.min(10, Math.max(1, n)) : undefined });
          }}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor={`${id}-b`} className="text-xs">
          Budget each
        </FieldLabel>
        <Input
          id={`${id}-b`}
          type="number"
          min={0}
          step="0.01"
          placeholder="No limit"
          value={policy.budget_micros === undefined ? "" : policy.budget_micros / 1_000_000}
          onChange={(e) => {
            const n = Number.parseFloat(e.target.value);
            set({
              budget_micros: Number.isFinite(n) && n >= 0 ? Math.round(n * 1_000_000) : undefined,
            });
          }}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor={`${id}-k`} className="text-xs">
          Task kinds
        </FieldLabel>
        <Input
          id={`${id}-k`}
          placeholder="All"
          defaultValue={policy.allowed_task_kinds?.join(", ") ?? ""}
          onBlur={(e) => {
            const kinds = e.target.value
              .split(",")
              .map((k) => k.trim())
              .filter(Boolean);
            set({ allowed_task_kinds: kinds.length ? kinds : undefined });
          }}
        />
      </Field>
    </div>
  );
}
